/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteLanguageSelect from "components/SiteLanguageSelect";

// @mui material components
import Card from "@mui/material/Card";
import AppleIcon from "@mui/icons-material/Apple";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import ParticlesBackground from "layouts/authentication/components/ParticlesBackground";

import MDSnackbar from "components/MDSnackbar";
import { LoginCall, SocialAuth } from "../../../redux/api/auth";
import { store } from "../../../redux/store";
import { AUTH_ERROR_SESSION_KEY } from "../../../utils/logout";
import {
  getAppleWebSignInConfig,
  performAppleWebSignIn,
} from "../../../utils/appleSignInWeb";
import {
  getGoogleWebSignInConfig,
  setupGoogleSignInButton,
} from "../../../utils/googleSignInWeb";
import { setGoogleSignupPrefill } from "../sign-up/googleSignupPrefill";

/*
  Shared horizontal ceiling for the SIGN IN button and the Google
  Sign-In button. GIS clamps its `renderButton` width to [200, 400] —
  pinning both buttons to the same `maxWidth` and letting them
  flex-grow inside their column makes them visually identical at every
  viewport (the column drives below 400, GIS's cap drives above).
*/
const AUTH_BUTTON_MAX_PX = 400;

function Basic() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [appleLoading, setAppleLoading] = useState(false);
  const appleConfigured = getAppleWebSignInConfig().enabled;
  const googleConfigured = getGoogleWebSignInConfig().enabled;
  const googleButtonRef = useRef(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  /*
    Temporarily hide the "Sign in with Apple" CTA on the web dashboard.
    Flip to `true` to bring it back without touching anything else —
    the handler, state, imports, and JSX are all left intact below.
  */
  const SHOW_APPLE_SIGN_IN = false;
  /*
    Temporarily hide "Sign in with Google" on the dashboard.
    Flip to `true` to show the button again.
  */
  const SHOW_GOOGLE_SIGN_IN = false;

  const closeErrorSB = () => setErrorSB(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(AUTH_ERROR_SESSION_KEY);
      if (stored) {
        sessionStorage.removeItem(AUTH_ERROR_SESSION_KEY);
        setErrorMessage(stored);
        setErrorSB(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const onClickLogin = async () => {
    const body = {
      password,
      email: email?.toLowerCase(),
    };
    try {
      const response = await LoginCall(body);

      if (
        response?.status == 400 ||
        response?.status == 403 ||
        response?.status == 404
      ) {
        setErrorMessage(response?.message || t("auth.credentialError"));
        setErrorSB(true);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.log("pippo error", error);
    }
  };

  const onChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const onChangePassword = (e) => {
    setPassword(e.target.value);
  };

  /*
    Mirrors the mobile Google sign-in handler in
    `microlab/src/screens/landing/LoginScreen.tsx`:

      Google credential → POST auth/social_auth (provider: 'google')
        → 200 + access_token  → reload into the dashboard
        → 404                 → redirect to /authentication/sign-up
                                with the Google identity stashed in
                                sessionStorage so the sign-up screen
                                can prefill name/email AND skip OTP
                                (Google already verified the email)
        → 409 EMAIL_EXISTS_NOT_LINKED → tell the user to use email
                                login since the account exists with
                                a different auth provider
        → anything else        → generic error toast

    `googleBusy` is gated against double-clicks (the rendered Google
    button is its own DOM node so we can't disable it imperatively;
    we just no-op the callback while a previous round-trip is still
    in flight).
  */
  const handleGoogleCredential = async (credential) => {
    if (googleBusy) return;
    setGoogleBusy(true);
    try {
      const response = await SocialAuth({
        email: credential.email,
        social_auth_provider: "google",
        social_auth_id: credential.sub,
      });

      // SocialAuth dispatches setAuth() on a successful access_token;
      // a reload guarantees every layout-level effect picks up the
      // freshly-authenticated state without us having to thread it
      // through React Router state.
      if (
        response?.access_token &&
        store.getState().auth?.access_token
      ) {
        window.location.reload();
        return;
      }

      if (
        response?.status === 409 &&
        response?.code === "EMAIL_EXISTS_NOT_LINKED"
      ) {
        setErrorMessage(
          "An account with this email already exists. Please sign in with your email and password instead."
        );
        setErrorSB(true);
        return;
      }

      if (response?.status === 404) {
        // First-time Google user — funnel them through the sign-up
        // flow. We persist the Google identity (sub + verified email
        // + names) to sessionStorage so the sign-up screen can skip
        // OTP and prefill the profile fields. See `googleSignupPrefill`.
        setGoogleSignupPrefill({
          email: credential.email.toLowerCase(),
          firstName:
            credential.givenName ||
            (credential.name || "").split(" ")[0] ||
            "",
          lastName:
            credential.familyName ||
            (credential.name || "").split(" ").slice(1).join(" ") ||
            "",
          socialAuthId: credential.sub,
        });
        navigate("/authentication/sign-up");
        return;
      }

      if (response?.access_token) {
        // Token returned but role wasn't allowed — same gate as
        // `LoginCall` enforces. Surface the same message we already
        // use for password login that lands on a non-dashboard role.
        setErrorMessage(
          "Your account is not allowed on this dashboard. Please contact an admin."
        );
        setErrorSB(true);
        return;
      }

      setErrorMessage(
        response?.message ||
          "Google sign-in failed. Please try again or use email and password."
      );
      setErrorSB(true);
    } catch (err) {
      setErrorMessage(
        err?.message ||
          "Google sign-in failed. Please try again or use email and password."
      );
      setErrorSB(true);
    } finally {
      setGoogleBusy(false);
    }
  };

  /*
    Mount the official Google button into our ref'd <div>. We only do
    this once — re-running the effect would call `initialize()` again,
    which under GIS overwrites the registered callback. The
    `unsubscribe` returned by `setupGoogleSignInButton` handles the
    teardown case where the user navigates away mid-flow.
  */
  useEffect(() => {
    if (!SHOW_GOOGLE_SIGN_IN || !googleConfigured || !googleButtonRef.current) return undefined;
    /*
      Measure the actual horizontal real-estate the Google button
      should fill, then clamp it to GIS's documented [200, 400] range.
      We read the *parent* element rather than the slot itself because
      the slot is a 0-height `<div ref>` until GIS finishes rendering
      its iframe — its `clientWidth` would be 0 on first paint. The
      parent MDBox already has the right padding/centering applied by
      the layout above, so its `clientWidth` is the truthful target.
    */
    const slot = googleButtonRef.current;
    const parent = slot?.parentElement;
    const measured = parent?.clientWidth ? parent.clientWidth : AUTH_BUTTON_MAX_PX;
    const widthPx = Math.max(200, Math.min(AUTH_BUTTON_MAX_PX, Math.round(measured)));

    let cleanup = null;
    (async () => {
      try {
        cleanup = await setupGoogleSignInButton({
          targetEl: googleButtonRef.current,
          onCredential: (credential) => {
            void handleGoogleCredential(credential);
          },
          onError: (err) => {
            setErrorMessage(
              err?.message || "Google sign-in failed. Please try again."
            );
            setErrorSB(true);
          },
          theme: "outline",
          text: "signin_with",
          width: widthPx,
        });
      } catch (err) {
        // Configuration / script-load failure surfaces here. We don't
        // toast on first paint — instead we leave the disabled-button
        // hint visible (handled in JSX below).
        // eslint-disable-next-line no-console
        console.warn("[GoogleSignInWeb] setup failed:", err?.message || err);
      }
    })();
    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [googleConfigured]);

  const handleAppleSignIn = async () => {
    if (!appleConfigured) {
      setErrorMessage(
        "Sign in with Apple is not enabled in this build. Add REACT_APP_APPLE_CLIENT_ID (and matching APPLE_WEB_CLIENT_ID on the API) to your environment, then rebuild."
      );
      setErrorSB(true);
      return;
    }
    setAppleLoading(true);
    try {
      const { idToken, sub, email: appleEmail } = await performAppleWebSignIn();
      const response = await SocialAuth({
        email: (appleEmail || "").toLowerCase().trim(),
        social_auth_provider: "apple",
        social_auth_id: sub,
        apple_identity_token: idToken,
      });
      if (store.getState().auth?.access_token) {
        window.location.reload();
        return;
      }
      if (response?.access_token) {
        setErrorMessage(
          "Your account is not allowed on this dashboard, or the session could not be started. Please contact an admin."
        );
        setErrorSB(true);
        return;
      }
      if (response?.status === 404) {
        setErrorMessage(
          "No Microlab account was found for this Apple ID. Use the email and password for your dashboard account, or contact an admin."
        );
      } else if (response?.status === 503) {
        setErrorMessage(
          "Sign in with Apple is not enabled on the server yet. Please use email and password, or try again later."
        );
      } else {
        setErrorMessage(
          response?.message || "Apple sign-in failed. Please try again or use email and password."
        );
      }
      setErrorSB(true);
    } catch (err) {
      const code = err?.error;
      if (code === "popup_closed_by_user" || err?.message?.includes("cancel")) {
        return;
      }
      setErrorMessage(err?.message || "Apple sign-in was cancelled or failed.");
      setErrorSB(true);
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <BasicLayout animation={<ParticlesBackground />}>
      <Card>
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translate(-50%, -60%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={require("../../../assets/images/logos/mini_logo.png")}
            style={{ height: 70, width: 70, borderRadius: 10 }}
            alt=""
          />
        </div>
        <MDBox mb={2} />
        <MDBox pt={4} pb={3} px={3}>
          <MDBox display="flex" justifyContent="flex-end" mb={1}>
            <SiteLanguageSelect
              sx={{
                color: "text.primary",
                bgcolor: "transparent",
                "& .MuiSvgIcon-root": { color: "text.primary" },
              }}
            />
          </MDBox>
          <MDBox component="form" role="form">
            <MDBox mb={2}>{t("auth.logInTitle")}</MDBox>
            <MDBox mb={2}>
              <MDInput
                onChange={onChangeEmail}
                type="email"
                label={t("auth.email")}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                onChange={onChangePassword}
                type="password"
                label={t("auth.password")}
                fullWidth
              />
            </MDBox>
            <MDBox mb={1} textAlign="right">
              <MDTypography
                component={Link}
                to="/authentication/forgot-password"
                variant="button"
                color="info"
                fontWeight="medium"
                sx={{ textDecoration: "none" }}
              >
                {t("auth.forgotPassword")}
              </MDTypography>
            </MDBox>
            {/*
              Cap the SIGN IN button at the same horizontal ceiling
              GIS uses for its rendered button (max 400 px). Without
              this, on wide form columns the SIGN IN button stretches
              past 400 while the Google button — which Google's SDK
              itself clamps at 400 — stays narrower, and the two read
              as misaligned. Pinning both to the same `maxWidth` and
              centering keeps them visually identical on every screen.
            */}
            <MDBox
              mt={4}
              mb={1}
              sx={{ maxWidth: AUTH_BUTTON_MAX_PX, mx: "auto" }}
            >
              <MDButton
                onClick={onClickLogin}
                variant="gradient"
                color="info"
                fullWidth
              >
                {t("auth.signIn")}
              </MDButton>
            </MDBox>

            {/*
              ─── Google Sign-In ──────────────────────────────────────
              We mirror the native mobile flow: the button is Google's
              official rendered button (brand-guideline requirement +
              the only path that emits an idToken on direct click).
              On a successful credential we POST to auth/social_auth
              with `social_auth_provider: 'google'` — see
              `handleGoogleCredential` above for the response branches.

              When the build doesn't have a client id configured we
              still render the slot, but show a small caption that
              tells the operator how to enable it. The DOM node for
              Google's button is empty in that state (no ref attaches
              to it) so the user simply doesn't see a button.
            */}
            {SHOW_GOOGLE_SIGN_IN && googleConfigured ? (
              /*
                Same `maxWidth` ceiling as the SIGN IN button above so
                the two pills align horizontally — see the comment on
                `AUTH_BUTTON_MAX_PX`. The inner `<div>` is the GIS
                target slot; on first paint it has no intrinsic width
                (Google's iframe hasn't been injected yet), so we
                give it `width: '100%'` to ensure its parent
                `clientWidth` reading is the column width — that's
                what we feed back to `renderButton({ width })` above.
              */
              <MDBox
                mt={1}
                mb={1}
                display="flex"
                justifyContent="center"
                sx={{
                  minHeight: 44,
                  maxWidth: AUTH_BUTTON_MAX_PX,
                  mx: "auto",
                }}
              >
                <div
                  ref={googleButtonRef}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    opacity: googleBusy ? 0.6 : 1,
                    pointerEvents: googleBusy ? "none" : "auto",
                  }}
                />
              </MDBox>
            ) : SHOW_GOOGLE_SIGN_IN ? (
              <MDBox mt={1} mb={1} textAlign="center">
                <MDTypography variant="caption" color="text">
                  Google sign-in is disabled in this build. Set{" "}
                  <code style={{ fontSize: "0.75rem" }}>
                    REACT_APP_GOOGLE_CLIENT_ID
                  </code>{" "}
                  to enable it.
                </MDTypography>
              </MDBox>
            ) : null}
            {/*
              Public web sign-up entry point — TEMPORARILY HIDDEN.

              The "New to Microlab? Create an account" CTA is commented
              out for now so users land on Sign In only and can't reach
              the /authentication/sign-up flow from the UI. The route
              itself still exists (and the page still works if visited
              directly), so re-enabling is just uncommenting this JSX
              when the registration flow is ready to ship publicly.

              Historical context: this CTA points at the new student
              sign-up flow gated behind RevenueCat annual billing — see
              `layouts/authentication/sign-up`. Admin / prof /
              university_admin accounts continue to be provisioned
              out-of-band, so disabling this entry point does not block
              any internal onboarding.
            */}
            {/*
            <MDBox mt={2} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                New to Microlab?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Create an account
                </MDTypography>
              </MDTypography>
            </MDBox>
            */}
            {SHOW_APPLE_SIGN_IN && (
              <>
                <MDBox mt={2} mb={0.5}>
                  <MDButton
                    type="button"
                    variant="contained"
                    color="dark"
                    fullWidth
                    disabled={appleLoading || !appleConfigured}
                    onClick={handleAppleSignIn}
                    startIcon={<AppleIcon sx={{ fontSize: 22 }} />}
                    sx={{
                      py: 1.1,
                      fontWeight: 600,
                      bgcolor: appleConfigured ? "#000" : "grey.600",
                      color: "#fff",
                      "&:hover": { bgcolor: appleConfigured ? "#1c1c1e" : "grey.700" },
                    }}
                  >
                    {appleLoading ? "Connecting…" : "Sign in with Apple"}
                  </MDButton>
                </MDBox>
                {!appleConfigured ? (
                  <MDBox mb={1} textAlign="center">
                    <MDTypography variant="caption" color="text">
                      Apple sign-in requires{" "}
                      <code style={{ fontSize: "0.75rem" }}>REACT_APP_APPLE_CLIENT_ID</code> in{" "}
                      <code style={{ fontSize: "0.75rem" }}>.env</code>.
                    </MDTypography>
                  </MDBox>
                ) : null}
              </>
            )}
            <MDSnackbar
              color="error"
              icon="warning"
              title={errorMessage || t("auth.credentialError")}
              dateTime=""
              content=""
              open={errorSB}
              onClose={closeErrorSB}
              close={closeErrorSB}
              bgWhite
            />
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
